import React, { useState, useEffect } from 'react';
import { searchKeepa, getMarketplaces, createProduct, createMapping, getProducts, startKeepaPull } from '../api';
import { loadMatchHierarchy, MatchCriterion } from './Settings';

// ── Auto-match helpers ────────────────────────────────────────────────────────

function titleSimilarity(a: string, b: string): number {
  const tokenize = (s: string) =>
    s.toLowerCase().replace(/[^a-z0-9\s]/g, '').split(/\s+/).filter(Boolean);
  const ta = tokenize(a);
  const tb = tokenize(b);
  if (ta.length === 0 || tb.length === 0) return 0;
  const setB = new Set<string>(tb);
  const inter = ta.filter(t => setB.has(t)).length;
  const union = new Set<string>(ta.concat(tb)).size;
  return inter / union;
}

interface AutoMatch {
  product: any;
  matchType: MatchCriterion['key'];
  confidence: 'high' | 'medium';
}

function runAutoMatch(
  keepaResults: KeepaResult[],
  internalProducts: any[],
  hierarchy: MatchCriterion[],
): Map<string, AutoMatch> {
  const map = new Map<string, AutoMatch>();
  const enabled = hierarchy.filter(c => c.enabled);

  for (const result of keepaResults) {
    for (const criterion of enabled) {
      let matched: any = null;

      if (criterion.key === 'ean') {
        const resultCodes = new Set<string>(
          (result.eans || []).concat(result.upcs || []).map(c => c.trim()).filter(Boolean)
        );
        matched = internalProducts.find(p => {
          if (p.ean && resultCodes.has(p.ean.trim())) return true;
          if (p.upc && resultCodes.has(p.upc.trim())) return true;
          return false;
        }) || null;
        if (matched) { map.set(result.asin, { product: matched, matchType: 'ean', confidence: 'high' }); break; }

      } else if (criterion.key === 'title') {
        let best = 0;
        let bestProd: any = null;
        for (const p of internalProducts) {
          const score = titleSimilarity(result.title || '', p.title || '');
          if (score > best) { best = score; bestProd = p; }
        }
        if (best >= 0.6) {
          map.set(result.asin, { product: bestProd, matchType: 'title', confidence: best >= 0.8 ? 'high' : 'medium' });
          break;
        }

      } else if (criterion.key === 'asin') {
        matched = internalProducts.find(p =>
          Array.isArray(p.mappings) && p.mappings.some((m: any) => m.asin === result.asin)
        ) || null;
        if (matched) { map.set(result.asin, { product: matched, matchType: 'asin', confidence: 'high' }); break; }
      }
    }
  }

  return map;
}

interface KeepaResult {
  asin: string;
  title: string;
  brand: string | null;
  eans: string[];
  upcs: string[];
  attributes: Record<string, any>;
  amazon_url: string | null;
  image_url: string | null;
}

interface Marketplace {
  id: number;
  code: string;
  domain: string;
}

interface KeepaSearchProps {
  onCreated?: (productId: string, jobId?: string) => void;
}

const KeepaSearch: React.FC<KeepaSearchProps> = ({ onCreated }) => {
  const [query, setQuery] = useState('');
  const [marketplace, setMarketplace] = useState('GB');
  const [marketplaces, setMarketplaces] = useState<Marketplace[]>([]);
  const [results, setResults] = useState<KeepaResult[]>([]);
  const [matchMap, setMatchMap] = useState<Map<string, AutoMatch>>(new Map());
  const [internalProducts, setInternalProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchDone, setSearchDone] = useState(false);
  const [linkModal, setLinkModal] = useState<{ result: KeepaResult; show: boolean } | null>(null);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [linkMode, setLinkMode] = useState<'existing' | 'new'>('new');
  const [newProductData, setNewProductData] = useState({ internal_sku: '', title: '', brand: '', ean: '' });

  useEffect(() => {
    loadMarketplaces();
    loadInternalProducts();
  }, []);

  const loadMarketplaces = async () => {
    try {
      const res = await getMarketplaces();
      setMarketplaces(res.data);
    } catch (err) {
      console.error('Failed to load marketplaces:', err);
    }
  };

  const loadInternalProducts = async () => {
    try {
      const res = await getProducts({ page_size: 200 });
      setInternalProducts(res.data.items || []);
    } catch (err) {
      console.error('Failed to load internal products:', err);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setError('');
    setSuccess('');
    setResults([]);
    setMatchMap(new Map());
    setSearchDone(false);

    try {
      const res = await searchKeepa(query, marketplace);
      const raw: KeepaResult[] = res.data.results || [];

      // Auto-match against internal products using configured hierarchy
      const hierarchy = loadMatchHierarchy();
      const matches = runAutoMatch(raw, internalProducts, hierarchy);
      setMatchMap(matches);

      // Sort: high-confidence matches first, then medium, then unmatched
      const sorted = [...raw].sort((a, b) => {
        const ma = matches.get(a.asin);
        const mb = matches.get(b.asin);
        const rank = (m: AutoMatch | undefined) =>
          !m ? 2 : m.confidence === 'high' ? 0 : 1;
        return rank(ma) - rank(mb);
      });

      setResults(sorted);
      setSearchDone(true);
    } catch (err: any) {
      const msg = err.response?.data?.detail || 'Search failed. Make sure the backend is running.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const openLinkModal = (result: KeepaResult) => {
    setSuccess('');
    setError('');
    setNewProductData({
      internal_sku: result.asin,
      title: result.title,
      brand: result.brand || '',
      ean: result.eans?.[0] || '',
    });

    const autoMatch = matchMap.get(result.asin);
    if (autoMatch) {
      setLinkMode('existing');
      setSelectedProductId(autoMatch.product.id);
    } else {
      setLinkMode('new');
      setSelectedProductId('');
    }

    setLinkModal({ result, show: true });
  };

  const handleLinkToExisting = async () => {
    if (!linkModal || !selectedProductId) return;
    const mp = marketplaces.find((m) => m.code === marketplace);
    if (!mp) return;

    try {
      await createMapping(selectedProductId, {
        marketplace_id: mp.id,
        asin: linkModal.result.asin,
        amazon_title: linkModal.result.title,
        amazon_brand: linkModal.result.brand || '',
        amazon_url: linkModal.result.amazon_url || `https://${mp.domain}/dp/${linkModal.result.asin}`,
      });

      // Trigger Keepa pull to refresh data for the newly linked ASIN
      let jobId: string | undefined;
      try {
        const pullRes = await startKeepaPull(marketplace, undefined, [selectedProductId]);
        jobId = pullRes.data.job_id;
      } catch {
        // pull failed — user can re-pull manually
      }

      setLinkModal(null);
      if (onCreated) {
        onCreated(selectedProductId, jobId);
      } else {
        setSuccess(`ASIN ${linkModal.result.asin} linked to product successfully!`);
      }
    } catch (err: any) {
      const status = err.response?.status;
      const detail: string = err.response?.data?.detail || '';
      // 409 with same ASIN = already linked; treat as success and navigate
      if (status === 409 && detail.includes(linkModal.result.asin)) {
        let jobId: string | undefined;
        try {
          const pullRes = await startKeepaPull(marketplace, undefined, [selectedProductId]);
          jobId = pullRes.data.job_id;
        } catch { /* ignore */ }
        setLinkModal(null);
        if (onCreated) { onCreated(selectedProductId, jobId); return; }
      }
      setError(detail || 'Failed to create mapping');
    }
  };

  const handleCreateAndLink = async () => {
    if (!linkModal) return;
    const mp = marketplaces.find((m) => m.code === marketplace);
    if (!mp) return;

    try {
      const productRes = await createProduct({
        internal_sku: newProductData.internal_sku,
        title: newProductData.title,
        brand: newProductData.brand || undefined,
        ean: newProductData.ean || undefined,
      });
      const productId = productRes.data.id;

      await createMapping(productId, {
        marketplace_id: mp.id,
        asin: linkModal.result.asin,
        amazon_title: linkModal.result.title,
        amazon_brand: linkModal.result.brand || '',
        amazon_url: linkModal.result.amazon_url || `https://${mp.domain}/dp/${linkModal.result.asin}`,
      });

      // Trigger Keepa pull so price/fee/image data is fetched for the linked ASIN
      let jobId: string | undefined;
      try {
        const pullRes = await startKeepaPull(marketplace, undefined, [productId]);
        jobId = pullRes.data.job_id;
      } catch {
        // pull failed — user can re-pull manually from product detail
      }

      setLinkModal(null);
      if (onCreated) {
        onCreated(productId, jobId);
      } else {
        setSuccess(`Product created and ASIN ${linkModal.result.asin} linked!`);
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to create product and mapping');
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2>Keepa Product Search</h2>
        <p>Search Amazon products by ASIN, EAN/UPC code, product title, or brand. Supports batch: comma-separated ASINs or codes (up to 100).</p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <form onSubmit={handleSearch}>
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="Search by ASIN, EAN/UPC, product title, or brand..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <select
            className="search-input"
            value={marketplace}
            onChange={(e) => setMarketplace(e.target.value)}
          >
            {marketplaces.length > 0 ? (
              marketplaces.map((m) => (
                <option key={m.code} value={m.code}>
                  {m.code} - {m.domain}
                </option>
              ))
            ) : (
              <>
                <option value="GB">GB - amazon.co.uk</option>
                <option value="US">US - amazon.com</option>
                <option value="DE">DE - amazon.de</option>
                <option value="FR">FR - amazon.fr</option>
                <option value="IT">IT - amazon.it</option>
                <option value="ES">ES - amazon.es</option>
              </>
            )}
          </select>
          <button type="submit" className="btn btn-primary" disabled={loading || !query.trim()}>
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </form>

      {loading && (
        <div className="loading">
          <div className="spinner"></div>
          Searching Keepa API...
        </div>
      )}

      {searchDone && results.length === 0 && !loading && (
        <div className="empty-state">
          <p style={{ fontSize: '24px', marginBottom: '8px' }}>No Results</p>
          <p>No products found for "{query}" on {marketplace}</p>
        </div>
      )}

      {results.length > 0 && (
        <div>
          <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
              Found {results.length} result{results.length !== 1 ? 's' : ''} for &ldquo;{query}&rdquo; on {marketplace}
            </span>
            {matchMap.size > 0 && (
              <span style={{
                fontSize: '12px', fontWeight: 600,
                color: 'var(--success)',
                background: 'rgba(63,185,80,0.1)', border: '1px solid rgba(63,185,80,0.25)',
                padding: '2px 10px', borderRadius: '20px',
              }}>
                {matchMap.size} auto-matched
              </span>
            )}
          </div>
          <div className="results-grid">
            {results.map((r, idx) => {
              const match = matchMap.get(r.asin);
              const isBestMatch = match?.confidence === 'high';
              const isLikelyMatch = match?.confidence === 'medium';
              return (
                <div
                  key={`${r.asin}-${idx}`}
                  className="result-card"
                  style={isBestMatch ? {
                    border: '1.5px solid var(--accent)',
                    boxShadow: '0 0 0 3px var(--accent-soft)',
                  } : isLikelyMatch ? {
                    border: '1.5px solid var(--warning, #d29922)',
                  } : undefined}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                      <span className="result-asin">{r.asin}</span>
                      {isBestMatch && (
                        <span style={{
                          fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                          color: 'var(--accent)', background: 'var(--accent-soft)',
                          border: '1px solid var(--border-accent)',
                          padding: '1px 7px', borderRadius: '20px',
                        }}>
                          Best Match
                        </span>
                      )}
                      {isLikelyMatch && (
                        <span style={{
                          fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em',
                          color: 'var(--warning, #d29922)', background: 'rgba(210,153,34,0.1)',
                          border: '1px solid rgba(210,153,34,0.3)',
                          padding: '1px 7px', borderRadius: '20px',
                        }}>
                          Likely Match
                        </span>
                      )}
                    </div>
                    {r.amazon_url && (
                      <a href={r.amazon_url} target="_blank" rel="noopener noreferrer" className="link" style={{ fontSize: '12px', flexShrink: 0 }}>
                        View on Amazon
                      </a>
                    )}
                  </div>
                  <div className="result-title">{r.title || 'No title available'}</div>
                  {r.brand && <div className="result-brand">Brand: {r.brand}</div>}
                  {(r.eans?.length > 0 || r.upcs?.length > 0) && (
                    <div className="result-eans">
                      {r.eans?.length > 0 && `EAN: ${r.eans.join(', ')}`}
                      {r.eans?.length > 0 && r.upcs?.length > 0 && ' · '}
                      {r.upcs?.length > 0 && `UPC: ${r.upcs.join(', ')}`}
                    </div>
                  )}
                  {match && (
                    <div style={{
                      fontSize: '11.5px', color: 'var(--text-muted)',
                      background: 'var(--bg-card-hover)',
                      border: '1px solid var(--border-primary)',
                      borderRadius: 'var(--radius-sm)',
                      padding: '5px 8px', marginTop: '6px',
                      display: 'flex', alignItems: 'center', gap: '5px',
                    }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                      </svg>
                      <span>Matches <strong>{match.product.internal_sku}</strong> — {match.product.title}</span>
                      <span style={{
                        marginLeft: 'auto', fontSize: '10px', fontWeight: 600,
                        color: 'var(--text-muted)', background: 'var(--bg-secondary)',
                        padding: '1px 5px', borderRadius: '4px',
                      }}>
                        via {match.matchType === 'ean' ? 'EAN/UPC' : match.matchType === 'title' ? 'Title' : 'ASIN'}
                      </span>
                    </div>
                  )}
                  <div className="result-actions">
                    <button className="btn btn-primary btn-small" onClick={() => openLinkModal(r)}>
                      {match ? 'Link Matched Product' : 'Link to Product'}
                    </button>
                    {r.amazon_url && (
                      <a href={r.amazon_url} target="_blank" rel="noopener noreferrer">
                        <button className="btn btn-secondary btn-small">Amazon</button>
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Link Modal */}
      {linkModal?.show && (
        <div className="modal-overlay" onClick={() => setLinkModal(null)}>
          <div className="card" style={{ maxWidth: '560px' }} onClick={(e) => e.stopPropagation()}>
            <div className="card-header">
              <span className="card-title">Link ASIN {linkModal.result.asin}</span>
              <button className="modal-close" onClick={() => setLinkModal(null)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <div className="result-title" style={{ WebkitLineClamp: 'unset' as any }}>{linkModal.result.title}</div>
              {linkModal.result.brand && <div className="result-brand">Brand: {linkModal.result.brand}</div>}
            </div>

            {/* Auto-match callout */}
            {matchMap.get(linkModal.result.asin) && (() => {
              const m = matchMap.get(linkModal.result.asin)!;
              return (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  background: 'rgba(63,185,80,0.07)', border: '1px solid rgba(63,185,80,0.3)',
                  borderRadius: 'var(--radius-md)', padding: '8px 12px', marginBottom: '14px',
                  fontSize: '12.5px', color: 'var(--text-secondary)',
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                  <span>
                    Auto-matched to <strong>{m.product.internal_sku}</strong> via {m.matchType === 'ean' ? 'EAN/UPC' : m.matchType === 'title' ? 'title similarity' : 'ASIN'}.
                    {' '}You can override by switching tabs.
                  </span>
                </div>
              );
            })()}

            <div className="modal-tabs">
              <button
                className={`modal-tab ${linkMode === 'new' ? 'active' : ''}`}
                onClick={() => setLinkMode('new')}
              >
                Create New Product
              </button>
              <button
                className={`modal-tab ${linkMode === 'existing' ? 'active' : ''}`}
                onClick={() => setLinkMode('existing')}
              >
                Link to Existing
              </button>
            </div>

            {linkMode === 'new' ? (
              <div>
                <div className="form-group">
                  <label>Internal SKU *</label>
                  <input
                    value={newProductData.internal_sku}
                    onChange={(e) => setNewProductData({ ...newProductData, internal_sku: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    value={newProductData.title}
                    onChange={(e) => setNewProductData({ ...newProductData, title: e.target.value })}
                  />
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Brand</label>
                    <input
                      value={newProductData.brand}
                      onChange={(e) => setNewProductData({ ...newProductData, brand: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>EAN</label>
                    <input
                      value={newProductData.ean}
                      onChange={(e) => setNewProductData({ ...newProductData, ean: e.target.value })}
                    />
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-primary" onClick={handleCreateAndLink}>
                    Create Product & Link ASIN
                  </button>
                </div>
              </div>
            ) : (
              <div>
                <div className="form-group">
                  <label>Select Product</label>
                  <select
                    value={selectedProductId}
                    onChange={(e) => setSelectedProductId(e.target.value)}
                  >
                    <option value="">-- Select a product --</option>
                    {internalProducts.map((p: any) => (
                      <option key={p.id} value={p.id}>
                        {p.internal_sku} - {p.title}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-primary"
                    onClick={handleLinkToExisting}
                    disabled={!selectedProductId}
                  >
                    Link ASIN to Product
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default KeepaSearch;
